import { ReactElement, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { ISpecialistListFilters } from '1_shared/config/interfaces';
import { ChipsSelected, Typography } from '1_shared/ui';
import { ESortType } from '4_widgets/SpecialistFilters/ui/SpecialistFilters';

import { getDictionaries } from '../../../1_shared/api/dictionary';
import useSWRWithCache from '../../../1_shared/api/lib/useSWRWithCache';
import { IOption } from '../../../1_shared/config/interfaces/IOption';

import { IKeyThemesProps } from './interfaces/IKeyThemesProps';

import styles from './KeyThemes.module.scss';
import { useLocation, useNavigate } from 'react-router';

/*
  Компонент используется на главной странице в блоке под списком специалистов,
  в нем дублируется фильтрация по ключевым темам основного фильтра страницы,
  так же учитывается примененная фильтрация по ключевой теме при переходе из страницы
  специалиста, выбрав ту или иную тему, которая записывается в стейт, извлекается и
  применяется в фильтр, при удалении очищается стейт, если он существовал
 */
const KeyThemes = ({ onChange }: IKeyThemesProps): ReactElement => {
  const { control, handleSubmit, setValue } =
    useFormContext<ISpecialistListFilters>();

  const location = useLocation();
  const navigate = useNavigate();
  const stateData = location.state as { value?: string[] };

  const clearLocationState = () =>
    navigate(location.pathname, {
      state: null,
      replace: true,
    });

  const { data: keyThemesOptions } = useSWRWithCache<IOption[]>(
    '/spec/key-themes',
    getDictionaries,
  );

  const handleFormSubmit = (data: ISpecialistListFilters) => {
    const filters = {
      filter: {
        ...data,
        sortRequest: {
          order: data?.sortRequest.order ?? 'ASC',
          sortType: data?.sortRequest.order
            ? ESortType.BY_EXPERIENCE
            : ESortType.BY_NEAREST_SESSION,
        },
      },
    };
    onChange(filters);
  };

  const handleFieldChange = () => {
    handleSubmit(handleFormSubmit)();
  };

  const deleteThemas = (
    keyThemas: string[],
    value: string,
    onChange: (value: string[]) => void,
  ) => {
    const tempArr = keyThemas.filter(el => el !== value);
    onChange(tempArr);
    setValue('keyThemes', tempArr);
    if (stateData?.value) {
      clearLocationState();
    }
  };

  useEffect(() => {
    if (stateData?.value) {
      setValue('keyThemes', stateData.value);
    }
  }, [location.state]);

  return (
    <div className={styles.root}>
      <Typography type="title">Не смогли выбрать?</Typography>
      <Typography>Можно расширить поиск, убрав некоторые параметры</Typography>
      <Controller
        control={control}
        name="keyThemes"
        render={({ field: { value, onChange } }) => {
          const rusThemas: IOption[] = value
            ?.map(element =>
              keyThemesOptions?.find(
                (subElement: IOption) => subElement.value === element,
              ),
            )
            .filter((element): element is IOption => Boolean(element)); // фильтрация на случай, если в массиве попал undefined
          return (
            <div className={styles.chipsBlock}>
              {rusThemas?.map((thema: IOption) => (
                <ChipsSelected
                  key={thema.value}
                  value={thema.label}
                  onDelete={() => {
                    deleteThemas(value, thema.value, onChange);
                    handleFieldChange();
                  }}
                />
              ))}
            </div>
          );
        }}
      />
    </div>
  );
};

export default KeyThemes;
